/**
 * URL Validation Utilities
 * Provides robust URL validation and normalization functions
 */

/**
 * Validates if a string is a valid URL
 * @param {string} urlString - The URL string to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.protocols - Allowed protocols (default: ['http', 'https', 'ftp', 'ftps'])
 * @param {boolean} options.requireProtocol - Whether protocol is required (default: true)
 * @param {boolean} options.allowLocalhost - Whether to allow localhost URLs (default: true)
 * @param {boolean} options.allowIP - Whether to allow IP addresses (default: true)
 * @returns {boolean} - True if valid URL, false otherwise
 */
export const isValidUrl = (urlString, options = {}) => {
    // Handle edge cases
    if (!urlString || typeof urlString !== 'string') {
        return false;
    }

    // Trim whitespace
    const trimmedUrl = urlString.trim();

    if (trimmedUrl.length === 0) {
        return false;
    }

    // Default options
    const {
        protocols = ['http', 'https', 'ftp', 'ftps'],
        requireProtocol = true,
        allowLocalhost = true,
        allowIP = true,
    } = options;

    let urlToTest = trimmedUrl;

    // If protocol is not required and missing, add a default one for validation
    if (
        !requireProtocol &&
        !trimmedUrl.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//)
    ) {
        urlToTest = `https://${trimmedUrl}`;
    }

    try {
        const url = new URL(urlToTest);

        // Check if protocol is in allowed list
        const protocol = url.protocol.slice(0, -1); // Remove trailing ':'
        if (!protocols.includes(protocol)) {
            return false;
        }

        // Check hostname validity
        if (!url.hostname || url.hostname.length === 0) {
            return false;
        }

        // Check for localhost if not allowed
        if (
            !allowLocalhost &&
            (url.hostname === 'localhost' ||
                url.hostname === '127.0.0.1' ||
                url.hostname === '::1' ||
                url.hostname.endsWith('.local'))
        ) {
            return false;
        }

        // Check for IP addresses if not allowed
        if (!allowIP && isIPAddress(url.hostname)) {
            return false;
        }

        // Validate port if present
        if (url.port) {
            const portNum = parseInt(url.port, 10);
            if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                return false;
            }
        }

        // Additional validation: hostname should have at least one dot (unless localhost/IP)
        if (
            !allowLocalhost &&
            !isIPAddress(url.hostname) &&
            !url.hostname.includes('.')
        ) {
            return false;
        }

        return true;
    } catch {
        return false;
    }
};

/**
 * Validates if a string is a valid web URL (http/https only)
 * @param {string} urlString - The URL string to validate
 * @param {Object} options - Additional validation options
 * @returns {boolean} - True if valid web URL, false otherwise
 */
export const isValidWebUrl = (urlString, options = {}) => {
    return isValidUrl(urlString, {
        ...options,
        protocols: ['http', 'https'],
    });
};

/**
 * Validates if a string is a valid bookmark URL
 * Stricter validation for bookmark use cases
 * @param {string} urlString - The URL string to validate
 * @returns {boolean} - True if valid bookmark URL, false otherwise
 */
export const isValidBookmarkUrl = urlString => {
    // Bookmarks should be web URLs with proper domains
    return isValidWebUrl(urlString, {
        requireProtocol: true,
        allowLocalhost: false,
        allowIP: false,
    });
};

/**
 * Normalizes a URL string
 * @param {string} urlString - The URL string to normalize
 * @param {Object} options - Normalization options
 * @param {boolean} options.removeTrailingSlash - Remove trailing slash (default: true)
 * @param {boolean} options.lowercase - Convert to lowercase (default: true)
 * @param {boolean} options.removeWWW - Remove www prefix (default: false)
 * @param {boolean} options.addProtocol - Add https:// if missing (default: true)
 * @returns {string|null} - Normalized URL or null if invalid
 */
export const normalizeUrl = (urlString, options = {}) => {
    if (!urlString || typeof urlString !== 'string') {
        return null;
    }

    const {
        removeTrailingSlash = true,
        lowercase = true,
        removeWWW = false,
        addProtocol = true,
    } = options;

    let normalized = urlString.trim();

    // Add protocol if missing
    if (addProtocol && !normalized.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//)) {
        normalized = `https://${normalized}`;
    }

    try {
        const url = new URL(normalized);

        // Apply lowercase
        if (lowercase) {
            url.protocol = url.protocol.toLowerCase();
            url.hostname = url.hostname.toLowerCase();
        }

        // Remove www
        if (removeWWW && url.hostname.startsWith('www.')) {
            url.hostname = url.hostname.substring(4);
        }

        // Remove trailing slash from pathname
        if (
            removeTrailingSlash &&
            url.pathname.endsWith('/') &&
            url.pathname.length > 1
        ) {
            url.pathname = url.pathname.slice(0, -1);
        }

        return url.toString();
    } catch {
        return null;
    }
};

/**
 * Extracts the domain from a URL
 * @param {string} urlString - The URL string
 * @returns {string|null} - Domain or null if invalid
 */
export const extractDomain = urlString => {
    try {
        const url = new URL(urlString);
        return url.hostname;
    } catch {
        return null;
    }
};

/**
 * Checks if a string is an IP address (IPv4 or IPv6)
 * @param {string} str - The string to check
 * @returns {boolean} - True if IP address, false otherwise
 */
const isIPAddress = str => {
    // IPv4 pattern
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;

    // IPv6 pattern (simplified)
    const ipv6Pattern = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/;

    if (ipv4Pattern.test(str)) {
        // Validate IPv4 octets
        const octets = str.split('.');
        return octets.every(octet => {
            const num = parseInt(octet, 10);
            return num >= 0 && num <= 255;
        });
    }

    return ipv6Pattern.test(str);
};

/**
 * Gets a user-friendly error message for invalid URLs
 * @param {string} urlString - The URL string that failed validation
 * @returns {string} - Error message
 */
export const getUrlErrorMessage = urlString => {
    if (
        !urlString ||
        typeof urlString !== 'string' ||
        urlString.trim().length === 0
    ) {
        return 'URL cannot be empty';
    }

    const trimmed = urlString.trim();

    // Check for protocol
    if (!trimmed.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//)) {
        return 'URL must include a protocol (e.g., http:// or https://)';
    }

    // Check for spaces
    if (trimmed.includes(' ')) {
        return 'URL cannot contain spaces';
    }

    // Try to parse and provide specific error
    try {
        const url = new URL(trimmed);

        if (!url.hostname || url.hostname.length === 0) {
            return 'URL must include a valid domain';
        }

        if (!url.hostname.includes('.') && url.hostname !== 'localhost') {
            return 'URL must include a valid domain with a top-level domain (e.g., .com, .org)';
        }

        return 'URL format is invalid';
    } catch {
        return 'URL format is invalid';
    }
};
